import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import styled from 'styled-components';

import messages from './messages/LocalitySearchBox_messages';

import Button from '../../components/Button';
import { Input } from '../../components/Form';

const propTypes = {
  municipalities: PropTypes.object.isRequired,
  regions: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
  intl: intlShape.isRequired,
  disabled: PropTypes.bool.isRequired,
};

class LocalitySearchBox extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      wait: 100,
      queue: null,
      results: null,
      searchString: '',
    };
  }

  /* eslint-disable react/no-did-update-set-state */
  componentDidUpdate() {
    const { disabled } = this.props;
    const { searchString, results } = this.state;
    if (disabled && (searchString !== '' || results !== null)) {
      this.setState({ searchString: '', results: null });
    }
  }
  /* eslint-enable react/no-did-update-set-state */

  get primaryNameKey() {
    const { locale: { lang }} = this.props;
    const nameKey = lang === 'en' ? 'name_fi' : `name_${lang}`;
    return nameKey;
  }

  get secondaryNameKey() {
    if (this.primaryNameKey === 'name_fi') return 'name_sv';
    return 'name_fi';
  }

  filterData = () => {
    const { municipalities, regions } = this.props;
    const { searchString } = this.state;
    const s = searchString.toLowerCase();
    const regionHits = {};
    const results = [];

    if (s.length < 2) {
      return this.setState({ results: null });
    }

    const generateHighlighted = fromThis => {
      const highlighted = [];
      let reminder = fromThis;
      while (reminder.toLowerCase().indexOf(s) !== -1) {
        const from = reminder.toLowerCase().indexOf(s);
        const length = s.length;
        const first = reminder.substr(0, from);
        highlighted.push(first);
        const second = reminder.substr(from, length);
        highlighted.push(<Highlight>{second}</Highlight>);
        reminder = reminder.substr(from + length);
      }
      highlighted.push(reminder);
      return <span>{highlighted}</span>;
    }

    Object.values(regions).forEach(r => {
      let label = r[this.primaryNameKey];
      if (r[this.primaryNameKey] !== r[this.secondaryNameKey]) {
        label = `${r[this.primaryNameKey]} / ${r[this.secondaryNameKey]}`;
      }
      if (label.toLowerCase().indexOf(s) !== -1) {
        regionHits[r.id] = {
          id: r.id,
          regionId: r.id,
          type: 'REGION',
          name_fi: r.name_fi,
          name_sv: r.name_sv,
          municipalities: [],
          label: (
            <Result>
              <ResultType>
                <FormattedMessage
                  {...messages.localitySearchTypeRegion}
                  info="Region"
                />
              </ResultType>
              <Value>
                {generateHighlighted(label)}
              </Value>
            </Result>
          ),
        };
      }
    });

    Object.values(municipalities).forEach(m => {
      let label = m[this.primaryNameKey];
      if (m[this.primaryNameKey] !== m[this.secondaryNameKey]) {
        label = `${m[this.primaryNameKey]} / ${m[this.secondaryNameKey]}`;
      }
      if (label.toLowerCase().indexOf(s) !== -1) {
        const highlighted = generateHighlighted(label);
        let region;
        Object.values(regions).forEach(r => {
          if (r.municipalities.indexOf(m.id) !== -1) {
            region = r;
            if (!regionHits[r.id]) {
              regionHits[r.id] = {
                id: r.id,
                regionId: r.id,
                type: 'REGION',
                name_fi: r.name_fi,
                name_sv: r.name_sv,
                municipalities: [],
                label: (
                  <Result>
                    <ResultType>
                      <FormattedMessage
                        {...messages.localitySearchTypeRegion}
                        info="Region"
                      />
                    </ResultType>
                    <Value>
                      {r[this.primaryNameKey]} / {r[this.secondaryNameKey]}
                    </Value>
                  </Result>
                ),
              };
              return false;
            }
          }
          return true;
        });
        const municipality = {
          id: m.id,
          regionId: region.id,
          municipalityId: m.id,
          type: 'MUNICIPALITY',
          name_fi: m.name_fi,
          name_sv: m.name_sv,
          label: (
            <Result>
              <ResultType>
                <FormattedMessage
                  {...messages.localitySearchTypeMunicipality}
                  info="Municipality"
                />
              </ResultType>
              <Value>
                {highlighted}
              </Value>
            </Result>
          ),
        };
        results.push(municipality);
        regionHits[region.id].municipalities.push(highlighted);
      }
    });
    Object.values(regionHits).forEach(r => {
      if (r.municipalities.length) {
        let subLabel = r.municipalities;
        if (r.municipalities.length > 1) {
          const sep = <span>, </span>;
          subLabel = r.municipalities.slice(1).reduce((acc, m) => {
            acc.push(sep);
            acc.push(m);
            return acc;
          }, [r.municipalities[0]]);
        }
        r.subLabel = (
          <SubResult>
            (<ResultType>
              <FormattedMessage
                {...messages.localitySearchTypeMunicipalities}
                info="Municipalities"
              />
            </ResultType>...{subLabel})
          </SubResult>
        );
      }
      results.unshift(r);
    })
    results.sort((a, b) => {
      if (a.type === 'REGION' && b.type === 'MUNICIPALITY') return -1;
      if (a.type === 'MUNICIPALITY' && b.type === 'REGION') return 1;
      const nameA = a[this.primaryNameKey];
      const nameB = b[this.primaryNameKey];
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    return this.setState({ results: results.length ? results : null });
  }

  search = evnt => {
    // Just a basic debouncer for actual filtering
    const { wait, queue } = this.state;
    clearTimeout(queue);
    const searchString = evnt.target.value;
    const newQueue = setTimeout(this.filterData, wait);
    this.setState({ searchString, queue: newQueue });
  }

  select = option => {
    const { onSelect } = this.props;
    this.setState({
      results: null,
      searchString: '',
    }, () => onSelect(option));
  }

  render() {
    const { results, searchString } = this.state;
    const { intl, disabled } = this.props;

    return (
      <div>
        <SearchInput
          id="locality-search-input"
          value={searchString}
          onChange={this.search}
          disabled={disabled}
          placeholder={
            intl.formatMessage(messages.localitySearchInputPlaceHolder)}
        />
        {results && (
          <div>
            {results.map(r =>
              <ResultButton
                data-type={r.type}
                key={`location-search-result-${r.type}-${r.id}`}
                kind='link'
                onClick={() => this.select(r)}
              >
                {r.label}
                {r.subLabel}
              </ResultButton>
            )}
          </div>
        )}
        {(!results && searchString.length > 0 && searchString.length < 2) && (
          <div>
            <span>
              <FormattedMessage
                {...messages.localitySearchHintMinimumLength}
                info="Write at least 2 characters."
              />
            </span>
          </div>
        )}
        {(!results && searchString.length > 1) && (
          <div>
            <span>
              <FormattedMessage
                {...messages.localitySearchHintNoResults}
                info="No results."
                values={{
                  searchString
                }}
              />
            </span>
          </div>
        )}
      </div>
    );
  }
}

LocalitySearchBox.propTypes = propTypes;

const Highlight = styled.span`
  font-weight: bold;
`;

const Result = styled.span`
  display: flex;
  flex-direction: column;

  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const SubResult = styled.span`
  font-size: 80%;
  white-space: normal;

  @media (min-width: 640px) {
    margin-left: 6px;
  }
`;

const ResultButton = styled(Button)`
  align-items: flex-start;
  border-bottom: 1px dotted ${props => props.theme['grey-normal']};
  color: ${props => props.theme['text-black']};
  flex-direction: column;
  flex-wrap: wrap;
  font-weight: normal;
  justify-content: flex-start;
  margin: 3px 0px;
  padding: 3px;
  text-align: left;
  text-decoration: none;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const ResultType = styled.span`
  font-size: 80%;
  font-weight: bold;
  margin-right: 6px;
  text-transform: uppercase;
`;

const SearchInput = styled(Input)`
  box-sizing: border-box;
  width: 100%;
`;

const Value = styled.span`
  border-bottom: 3px solid ${props => props.theme['green-normal']};
  display: flex;
  white-space: normal;
`;

export default injectIntl(LocalitySearchBox);
